import {
  ColorPicker,
  Form,
  Input,
  message,
  Select,
  Upload,
  Button,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useState } from "react";
import { createBusiness } from "../actions/acc_actions";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const { Option } = Select;

export default function BusinessForm({ refetch }) {
  const accUrn = useSelector((state) => state.account.urn);
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const onFinish = async (values) => {
    const formData = new FormData();

    formData.append("name", values.name);
    formData.append("colors[brand]", values.brandColor);
    formData.append("colors[accent]", values.accentColor);
    formData.append("locations_hint", values.locations_hint);
    formData.append("contact[facebook_tag]", values.fbTag);
    formData.append("contact[instagram_tag]", values.instaTag);
    formData.append("contact[twitter_tag]", values.xTag);
    formData.append("contact[tiktok_tag]", values.tiktok_tag);
    formData.append("contact[email]", values.email);
    formData.append("contact[phone]", values.phone);
    formData.append("contact[website]", values.website);
    formData.append("category", values.category);
    formData.append("file", file);

    setIsLoading(true);

    await createBusiness(accUrn, formData)
      .then(() => {
        refetch();
        window.location.reload();
        message.success(t("business_added_success"));
      })
      .catch((err) => {
        message.error(t("something_went_wrong"));
      });

    setIsLoading(false);
  };

  const handleFileChange = (info) => {
    const { fileList } = info;
    if (fileList.length > 0) {
      setFile(fileList[0].originFileObj);
    }
  };

  const customRequest = ({ file, onSuccess }) => {
    onSuccess("ok");
    setFile(file);
  };

  return (
    <Form
      form={form}
      className="rewards-form-container"
      onFinish={onFinish}
      layout="vertical"
    >
      <div className="rewards-form-section">
        <h4>{t("details")}</h4>
        <div className="rewards-inputs">
          <div className="form-row">
            <Form.Item
              name="name"
              label={t("business_name")}
              rules={[{ required: true, message: t("business_name_required") }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="category"
              label={t("category")}
              rules={[{ required: true, message: t("category_required") }]}
            >
              <Select style={{ width: "100%" }}>
                <Option value="FOOD_AND_DRINK">{t("food_and_drink")}</Option>
              </Select>
            </Form.Item>
          </div>
        </div>
      </div>

      <div className="rewards-form-section">
        <h4>{t("branding")}</h4>
        <div className="rewards-inputs">
          <Form.Item style={{ marginBottom: "2rem" }} label={t("select_logo")}>
            <Upload
              customRequest={customRequest}
              onChange={handleFileChange}
              maxCount={1}
              accept="image/*"
            >
              <Button variant="secondary" icon={<UploadOutlined />}>
                {t("upload_image")}
              </Button>
            </Upload>
          </Form.Item>

          <div className="form-row">
            <Form.Item
              name="brandColor"
              label={t("pick_brand_color")}
              rules={[{ required: true, message: t("brand_color_required") }]}
            >
              <ColorPicker
                style={{ width: "100%" }}
                showText
                onChange={(color) =>
                  form.setFieldsValue({ brandColor: color.toHexString() })
                }
              />
            </Form.Item>

            <Form.Item
              name="accentColor"
              label={t("text_color")}
              rules={[{ required: true, message: t("accent_color_required") }]}
            >
              <ColorPicker
                style={{ width: "100%" }}
                showText
                onChange={(color) =>
                  form.setFieldsValue({ accentColor: color.toHexString() })
                }
              />
            </Form.Item>
          </div>
        </div>
      </div>

      <div className="rewards-form-section">
        <h4>{t("location")}</h4>
        <div className="rewards-inputs">
          <Form.Item
            name="locations_hint"
            label={t("location_hint")}
            rules={[{ required: true, message: t("location_hint_required") }]}
          >
            <Input />
          </Form.Item>
        </div>
      </div>

      <div className="rewards-form-section">
        <h4>{t("social")}</h4>
        <div className="rewards-inputs">
          <Form.Item name="fbTag" label={t("facebook_tag")}>
            <Input />
          </Form.Item>

          <Form.Item name="instaTag" label={t("instagram_tag")}>
            <Input />
          </Form.Item>

          <Form.Item name="xTag" label={t("x_tag")}>
            <Input />
          </Form.Item>

          <Form.Item name="tiktok_tag" label={t("tiktok_tag")}>
            <Input />
          </Form.Item>
        </div>
      </div>

      <div className="rewards-form-section">
        <h4>{t("contact")}</h4>
        <div className="rewards-inputs">
          <Form.Item
            name="email"
            label={t("customer_email")}
            rules={[
              { required: true, message: t("email_required") },
              { type: "email", message: t("email_invalid") },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label={t("customer_phone")}
            rules={[{ required: true, message: t("phone_required") }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="website" label={t("website_address")}>
            <Input />
          </Form.Item>
        </div>
      </div>

      <Form.Item className="rewards-form-btn">
        <Button
          loading={isLoading}
          type="primary"
          htmlType="submit"
          style={{ width: "12rem" }}
        >
          {isLoading ? t("submitting") : t("submit")}
        </Button>
      </Form.Item>
    </Form>
  );
}
